import React from "react";
import { Route } from "../common/ObjectTypes";
import { useTranslation } from "react-i18next";
import { getRouteStatus, getStatusDotColor } from "./getRouteStatus";
import { HoverTooltip } from "../common/HoverTooltip";
import { routeDetailsClasses } from "../common/tailwindClassnames";
import { routeDisplayName } from "./routeDisplayName";
import { ROUTE_DETAIL_TABS } from "./routeSubpages";

type RouteDetailsProps = {
    route: Route,
    // The provider integration's on/off state: a paused Route's last-known health
    // means nothing, so the dot goes gray while this is false.
    providerEnabled?: boolean,
    // Unsaved edit of the Route's own name (decoupled from the provider's) —
    // shown here so the header tracks what the General tab is typing.
    routeName?: string,
    subPage: string | undefined,
    onSelectSubPage: Function,
}

const RouteDetails = ({
    route,
    providerEnabled = true,
    routeName,
    subPage,
    onSelectSubPage,
}: RouteDetailsProps) => {
    const { t } = useTranslation();
    // A disabled Route reports its last analysis, not its present — show it as
    // "Disabled" (gray), the same way the flow map suppresses health while off.
    const dotStatus = providerEnabled ? route.status : "disabled";

    return (
        <div className={routeDetailsClasses.container}>
            <div className={routeDetailsClasses.header}>
                <div className="max-w-list-table mx-auto">
                {/* page header — the Route's on/off switch lives on the flow map's
                    outgoing arrow, not here. */}
                <div className="flex flex-col md:flex-row md:justify-between md:items-start">
                    <div className="flex flex-col md:w-3/4">
                        <div className="flex flex-row items-center gap-3">
                            <h1 className={routeDetailsClasses.title}>{routeName ?? routeDisplayName(route)}</h1>
                            {/* The shared health dot (getStatusDotColor — same mapping as the
                                flow map panel), so the status stays visible on Sources/Logs
                                where the map isn't. */}
                            {dotStatus && (
                                /* Focusable (the default): the dot is the header's only
                                   status signal, so its text must be reachable by keyboard
                                   and assistive tech, not hover-only. */
                                <HoverTooltip content={getRouteStatus(dotStatus, true, t).text} position="top" portal>
                                    <span
                                        data-testid="RouteDetails-status-dot"
                                        className={`block w-2.5 h-2.5 rounded-full ${getStatusDotColor(dotStatus)}`}
                                    />
                                </HoverTooltip>
                            )}
                        </div>
                        <div className="flex flex-row items-center gap-2 mt-2 mb-3">
                            {route?.provider?.type?.name && <span className={routeDetailsClasses.headerPill}>{route.provider.type.name}</span>}
                            {route?.owner?.name && <span className={routeDetailsClasses.headerPill}>{route.owner.name}</span>}
                        </div>
                    </div>
                </div>
                {/* tabs for sub-pages — bidirectional redesign: technology-named tabs */}
                <div className="w-full flex flex-row font-semibold -ml-3" role="tablist">
                    {ROUTE_DETAIL_TABS.map(({ slug, labelKey }) => (
                        <label key={slug}
                            className={(subPage === slug ? routeDetailsClasses.tabActive : routeDetailsClasses.tabInactive).replace('mr-3', 'mx-3')}
                            data-testid={`RouteDetails-${slug}-tab`}
                            role="tab"
                            tabIndex={0}
                            aria-selected={subPage === slug}
                            onClick={() => onSelectSubPage(slug)}
                            onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); onSelectSubPage(slug); } }}>
                            {t(labelKey)}
                        </label>
                    ))}
                </div>
                </div>
            </div>
        </div>
    );
};

export default RouteDetails;